import type { ReactNode } from 'react';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { UndrawIllustration } from '../common/UndrawIllustration';
import type { UndrawScene } from '@/lib/undraw-illustrations';

interface LoginBackgroundProps {
  readonly children: ReactNode;
  /** When set, shows unDraw beside the form on lg+; omit for full-bleed photo (login). */
  readonly illustration?: UndrawScene;
}

/** Auth background image path (in public folder, served at /images/auth-bg.jpg) */
const AUTH_BG_IMAGE = '/images/auth-bg.jpg';

/**
 * Auth pages background: full-bleed image with dark overlay, or split layout with unDraw (lg+).
 * Used by Login, Sign up, and Forgot password.
 */
export function LoginBackground({ children, illustration }: LoginBackgroundProps) {
  if (!illustration) {
    return (
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 sm:px-6 lg:px-8">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${AUTH_BG_IMAGE})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-black/50" aria-hidden />

        <div className="absolute top-4 right-4 z-20 sm:top-6 sm:right-6">
          <LanguageSwitcher variant="auth" />
        </div>

        <div className="relative z-10 w-full max-w-md">{children}</div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen overflow-hidden">
      <div
        className="relative hidden min-h-screen w-0 flex-1 flex-col justify-center bg-muted/40 px-10 py-12 lg:flex lg:max-w-[50%] lg:w-1/2"
        aria-hidden
      >
        <div className="mx-auto w-full max-w-lg">
          <UndrawIllustration
            scene={illustration}
            decorative
            className="h-[min(420px,55vh)] w-full"
          />
        </div>
      </div>

      <div className="relative flex min-h-screen flex-1 items-center justify-center px-4 sm:px-6 lg:px-8">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat lg:hidden"
          style={{ backgroundImage: `url(${AUTH_BG_IMAGE})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-black/50 lg:bg-background" aria-hidden />

        <div className="absolute top-4 right-4 z-20 sm:top-6 sm:right-6">
          <LanguageSwitcher variant="auth" />
        </div>

        <div className="relative z-10 w-full max-w-md">{children}</div>
      </div>
    </div>
  );
}
